import { Box, SxProps, Theme } from '@mui/material';
import Select, { components, createFilter, MultiValueProps, MultiValueRemoveProps, StylesConfig } from 'react-select';

import { SimpleViewDto } from '../../common/dto/common.dto';
import { LoaderHorizontal } from './Loader';

export interface DropdownOption extends SimpleViewDto {
  color?: string;
}

const DEFAULT_CONTROL_HEIGHT = 30;
/** Above MUI modal (1300) so menus render over filter poppers/drawers. */
const DEFAULT_MENU_Z_INDEX = 1400;

const selectStyles = (
  controlHeight: number,
  limitVisibleValues: boolean,
  menuZIndex: number,
  multiValueLabelMaxWidth: number,
): StylesConfig<DropdownOption, boolean> => {
  return {
    control: (provided) => ({
      ...provided,
      minHeight: controlHeight,
      height: controlHeight,
      ...(limitVisibleValues ? { overflow: 'hidden' } : {}),
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: controlHeight,
      padding: '0 6px',
      ...(limitVisibleValues ? { flexWrap: 'nowrap', overflow: 'hidden' } : {}),
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: controlHeight,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: controlHeight <= 30 ? 4 : 8,
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.data.color,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      ...(limitVisibleValues
        ? {
            maxWidth: multiValueLabelMaxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }
        : {}),
    }),
    menuPortal: (base) => ({ ...base, zIndex: menuZIndex }),
  };
};

const LimitedMultiValue = <T extends SimpleViewDto>(maxVisible: number) => {
  const MultiValue = (props: MultiValueProps<T, true>) => {
    const { index, getValue } = props;
    const selected = getValue();
    const overflowCount = selected.length - maxVisible;

    if (index < maxVisible) {
      return <components.MultiValue {...props} />;
    }

    if (index === maxVisible) {
      const overflowNames = selected
        .slice(maxVisible)
        .map((item) => item.name)
        .join(', ');
      return (
        <Box
          component="span"
          title={overflowNames}
          sx={{
            alignSelf: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            borderRadius: '2px',
            fontSize: 12,
            lineHeight: 1.4,
            ml: '2px',
            px: 0.75,
            py: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          +{overflowCount} more
        </Box>
      );
    }

    return null;
  };

  return MultiValue;
};

const MinSelectedMultiValueRemove = <T extends SimpleViewDto>(minSelected: number) => {
  const MultiValueRemove = (props: MultiValueRemoveProps<T, true>) => {
    const selected = Array.isArray(props.selectProps.value) ? props.selectProps.value : [];
    if (selected.length <= minSelected) {
      return null;
    }
    return <components.MultiValueRemove {...props} />;
  };

  return MultiValueRemove;
};

interface BaseProps<T> {
  options: T[];
  isLoading?: boolean;
  name: string;
  placeholder?: string;
  ignoreMinWidth?: boolean;
  /** Styles applied to the outer wrapper. */
  sx?: SxProps<Theme>;
  /** Height of the select control in px. */
  controlHeight?: number;
  /** z-index for the portaled menu (must be above filter/modal layers). */
  menuZIndex?: number;
}

type Props<T> =
  | (BaseProps<T> & {
      multiple?: false;
      value?: T | null | undefined;
      onSelected: (item: T) => void;
    })
  | (BaseProps<T> & {
      multiple: true;
      value?: T[] | null | undefined;
      onSelected: (item: T[]) => void;
      /** Cap visible chips; overflow shows as "+N more". */
      maxVisibleSelected?: number;
      /** Max width (px) for each selected chip label when chips are limited. */
      multiValueLabelMaxWidth?: number;
      /** Require at least this many selections (blocks clearing below the minimum). */
      minSelected?: number;
    });

const DEFAULT_MULTI_VALUE_LABEL_MAX_WIDTH = 140;

const DropdownPicker = <T extends SimpleViewDto>(props: Props<T>) => {
  if (props.isLoading) return <LoaderHorizontal />;

  const maxVisibleSelected = props.multiple ? props.maxVisibleSelected : undefined;
  const minSelected = props.multiple ? props.minSelected : undefined;
  const multiValueLabelMaxWidth = props.multiple && props.multiValueLabelMaxWidth != null ? props.multiValueLabelMaxWidth : DEFAULT_MULTI_VALUE_LABEL_MAX_WIDTH;
  const limitVisibleValues = Boolean(maxVisibleSelected && maxVisibleSelected > 0);
  const controlHeight = props.controlHeight ?? DEFAULT_CONTROL_HEIGHT;
  const menuZIndex = props.menuZIndex ?? DEFAULT_MENU_Z_INDEX;

  const commonProps = {
    id: props.name,
    classNamePrefix: 'react-select',
    name: props.name,
    getOptionLabel: (option: T) => option.name,
    getOptionValue: (option: T) => option.id,
    filterOption: createFilter({ stringify: (option) => option.label }),
    hideSelectedOptions: false,
    isClearable: false,
    menuPortalTarget: document.body,
    styles: { ...selectStyles(controlHeight, limitVisibleValues, menuZIndex, multiValueLabelMaxWidth) } as StylesConfig<T, boolean>,
    options: props.options,
    placeholder: props.placeholder ?? 'Search...',
    tabSelectsValue: false,
  };

  return (
    <Box
      sx={[
        {
          minWidth: props.ignoreMinWidth ? 'unset' : 200,
          maxWidth: props.ignoreMinWidth ? 'unset' : 300,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      {props.multiple ? (
        <Select<T, true>
          {...commonProps}
          isMulti
          onChange={(newValue) => {
            const next = newValue ? [...newValue] : [];
            if (minSelected != null && next.length < minSelected) {
              return;
            }
            props.onSelected(next);
          }}
          value={props.value ?? []}
          components={{
            ...(limitVisibleValues && maxVisibleSelected ? { MultiValue: LimitedMultiValue<T>(maxVisibleSelected) } : {}),
            ...(minSelected != null && minSelected > 0 ? { MultiValueRemove: MinSelectedMultiValueRemove<T>(minSelected) } : {}),
          }}
        />
      ) : (
        <Select<T, false>
          {...commonProps}
          onChange={(newValue) => {
            props.onSelected(newValue as T);
          }}
          value={props.value ?? null}
        />
      )}
    </Box>
  );
};

export default DropdownPicker;
